const STORAGE_KEY = 'annur-accessibility-preferences-v1';

const defaults = {
  locale: 'es',
  textScale: 0,
  lineHeight: 0,
  letterSpacing: 0,
  textAlign: 'original',
  fontWeight: 0,
  readableFont: false,
  largeCursor: false,
  lightContrast: false,
  highContrast: false,
  grayscale: false,
  readingGuide: false,
  readingMask: false,
  hideImages: false,
  highlightContent: false,
  reduceMotion: false,
  highlightLinks: false,
};

const labels = {
  textScale: ['Pequeño', 'Predeterminado', 'Grande', 'Muy grande'],
  lineHeight: ['Predeterminado', 'Amplia', 'Muy amplia'],
  letterSpacing: ['Predeterminado', 'Medio', 'Alto'],
  textAlign: { original: 'Original', left: 'Izquierda', center: 'Centrada', right: 'Derecha' },
  fontWeight: ['Normal', 'Seminegrita', 'Negrita'],
};

class AnnurAccessibility {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-a11y-open]');
    this.panel = root.querySelector('[data-a11y-panel]');
    this.overlay = root.querySelector('[data-a11y-overlay]');
    this.status = root.querySelector('#AnnurAccessibilityStatus');
    this.jump = root.querySelector('[data-a11y-jump]');
    this.language = root.querySelector('[data-a11y-language]');
    this.state = this.readState();
    this.inerted = [];
  }

  readState() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      const state = { ...defaults, ...(saved && typeof saved === 'object' ? saved : {}) };
      state.locale = ['es', 'en', 'ar'].includes(state.locale) ? state.locale : 'es';
      return state;
    } catch {
      return { ...defaults };
    }
  }

  init() {
    this.bindEvents();
    this.translate();
    this.buildJumpOptions();
    this.apply(false);
  }

  bindEvents() {
    this.trigger.addEventListener('click', () => this.open());
    this.root.querySelector('[data-a11y-close]').addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.root.querySelector('[data-a11y-reset]').addEventListener('click', () => this.reset());
    this.root.querySelectorAll('[data-a11y-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.toggle(button.dataset.a11yToggle));
    });
    this.root.querySelectorAll('[data-a11y-step]').forEach((button) => {
      button.addEventListener('click', () => this.step(button.dataset.a11yStep, Number(button.dataset.direction)));
    });
    this.root.querySelectorAll('[data-a11y-cycle]').forEach((button) => {
      button.addEventListener('click', () => this.cycle(button.dataset.a11yCycle));
    });
    this.jump.addEventListener('change', () => this.jumpTo(this.jump.value));
    this.language.addEventListener('change', () => this.setLocale(this.language.value));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener('pointermove', (event) => this.updateReadingPosition(event.clientY), { passive: true });
    document.addEventListener('shopify:section:load', () => this.buildJumpOptions());
  }

  open() {
    this.overlay.hidden = false;
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.inerted = [...document.body.children]
      .filter((element) => element !== this.root && !element.hasAttribute('inert'))
      .map((element) => {
        element.inert = true;
        return element;
      });
    window.requestAnimationFrame(() => this.root.querySelector('[data-a11y-close]').focus());
  }

  close() {
    this.panel.hidden = true;
    this.overlay.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.inerted.forEach((element) => {
      element.inert = false;
    });
    this.inerted = [];
    this.trigger.focus();
  }

  handleKeydown(event) {
    if (this.panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('button:not([disabled]), select:not([disabled])')];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  toggle(key) {
    this.state[key] = !this.state[key];
    if (key === 'lightContrast' && this.state[key]) this.state.highContrast = false;
    if (key === 'highContrast' && this.state[key]) this.state.lightContrast = false;
    this.apply();
    this.announce(`${this.labelFor(key)} ${this.strings().messages[this.state[key] ? 'enabled' : 'disabled']}.`);
  }

  step(key, direction) {
    const limits = { textScale: [-1, 2], lineHeight: [0, 2] };
    const [min, max] = limits[key];
    this.state[key] = Math.min(max, Math.max(min, this.state[key] + direction));
    this.apply();
    this.announce(`${this.labelFor(key)}: ${this.valueFor(key)}.`);
  }

  cycle(key) {
    if (key === 'textAlign') {
      const values = ['original', 'left', 'center', 'right'];
      this.state[key] = values[(values.indexOf(this.state[key]) + 1) % values.length];
    } else {
      this.state[key] = (this.state[key] + 1) % 3;
    }
    this.apply();
    this.announce(`${this.labelFor(key)}: ${this.valueFor(key)}.`);
  }

  apply(persist = true) {
    const body = document.body;
    const html = document.documentElement;
    html.classList.remove('a11y-text-scale--1', 'a11y-text-scale-1', 'a11y-text-scale-2');
    if (this.state.textScale) html.classList.add(`a11y-text-scale-${this.state.textScale}`);
    const classMap = {
      readableFont: 'a11y-readable-font',
      largeCursor: 'a11y-large-cursor',
      lightContrast: 'a11y-light-contrast',
      highContrast: 'a11y-high-contrast',
      grayscale: 'a11y-grayscale',
      readingGuide: 'a11y-reading-guide',
      readingMask: 'a11y-reading-mask',
      hideImages: 'a11y-hide-images',
      highlightContent: 'a11y-highlight-content',
      reduceMotion: 'a11y-reduce-motion',
      highlightLinks: 'a11y-highlight-links',
    };
    Object.entries(classMap).forEach(([key, className]) => body.classList.toggle(className, this.state[key]));
    ['lineHeight', 'letterSpacing', 'fontWeight'].forEach((key) => {
      [1, 2].forEach((value) => body.classList.remove(`a11y-${this.cssName(key)}-${value}`));
      if (this.state[key]) body.classList.add(`a11y-${this.cssName(key)}-${this.state[key]}`);
    });
    ['left', 'center', 'right'].forEach((value) => body.classList.remove(`a11y-text-align-${value}`));
    if (this.state.textAlign !== 'original') body.classList.add(`a11y-text-align-${this.state.textAlign}`);
    this.root.querySelectorAll('[data-a11y-toggle]').forEach((button) => {
      const active = this.state[button.dataset.a11yToggle];
      button.setAttribute('aria-pressed', String(active));
    });
    this.root.querySelectorAll('[data-a11y-cycle]').forEach((button) => {
      const active = this.state[button.dataset.a11yCycle] !== 0 && this.state[button.dataset.a11yCycle] !== 'original';
      button.dataset.active = String(active);
    });
    this.root.querySelectorAll('[data-a11y-value]').forEach((output) => {
      output.textContent = this.valueFor(output.dataset.a11yValue);
    });
    window.dispatchEvent(
      new CustomEvent('annur:reduce-motion-change', { detail: { active: this.state.reduceMotion } }),
    );
    if (persist) this.save();
  }

  cssName(key) {
    return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
  }

  valueFor(key) {
    const value = this.state[key];
    const values = this.strings().values[key] || labels[key];
    return Array.isArray(values)
      ? values[value + (key === 'textScale' ? 1 : 0)]
      : values?.[value] || this.strings().values.textScale[1];
  }

  labelFor(key) {
    return this.strings().ui[key] || key;
  }

  strings() {
    return window.AnnurAccessibilityLocale.get(this.state.locale);
  }

  translate() {
    window.AnnurAccessibilityLocale.apply(this.root, this.state.locale);
    this.language.value = this.state.locale;
  }

  setLocale(locale) {
    this.state.locale = ['es', 'en', 'ar'].includes(locale) ? locale : 'es';
    this.translate();
    this.buildJumpOptions();
    this.apply();
    this.announce(this.strings().ui.title);
  }

  save() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {}
  }

  reset() {
    this.state = { ...defaults };
    this.jump.value = '';
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {}
    this.translate();
    this.apply(false);
    this.announce(this.strings().messages.reset);
  }

  updateReadingPosition(position) {
    if (!this.state.readingGuide && !this.state.readingMask) return;
    this.root.style.setProperty('--annur-a11y-reading-y', `${position}px`);
  }

  buildJumpOptions() {
    const targets = [
      [document.querySelector('header'), this.strings().jumpTargets.header],
      [document.querySelector('#MainContent'), this.strings().jumpTargets.main],
      ...[...document.querySelectorAll('#MainContent .shopify-section')].map((section) => [
        section,
        section.querySelector('h1, h2, h3')?.textContent?.trim(),
      ]),
      [document.querySelector('footer'), this.strings().jumpTargets.footer],
    ].filter(([element, label]) => element && label);
    const unique = new Map();
    targets.forEach(([element, label], index) => {
      if (!element.id) element.id = `annur-accessibility-target-${index}`;
      if (!unique.has(element.id)) unique.set(element.id, label.replace(/\s+/g, ' ').slice(0, 72));
    });
    this.jump.innerHTML = `<option value="">${this.strings().jumpPlaceholder}</option>`;
    unique.forEach((label, id) => this.jump.add(new Option(label, id)));
  }

  jumpTo(id) {
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.scrollIntoView({ behavior: this.state.reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(() => target.focus({ preventScroll: true }), 300);
    this.announce(`${this.strings().messages.moved} ${this.jump.selectedOptions[0].text}.`);
  }

  announce(message) {
    this.status.textContent = '';
    window.requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }
}

const initAccessibility = () =>
  document.querySelectorAll('[data-annur-accessibility]').forEach((root) => new AnnurAccessibility(root).init());

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAccessibility);
else initAccessibility();
